import './DeviceFrame.css'

import React from 'react'

import { TopBarType } from '@makyoapp/makyo-common'

import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useAppPagesContext } from '@/context/AppPagesContext'
import { useBuilderColorSchemeV2 } from '@/hooks/__project__/useBuilderColorSchemeV2'
import { useActiveDevice } from '@/hooks/useActiveDevice'

import { DesktopFrame } from './Desktop/DesktopFrame'
import { MobileFrame } from './Mobile/MobileFrame'
import { TabletFrame } from './Tablet/TabletFrame'

type DeviceFrameProps = {
  children: React.ReactNode
  isFullScreen: boolean
}

export const DeviceFrame = React.forwardRef<HTMLDivElement, DeviceFrameProps>(
  ({ children, isFullScreen }, deviceRef) => {
    const { deviceScale, statusBarColor } = useAppBuilderContext()
    const { activePage } = useAppPagesContext()
    const [viewport] = useActiveDevice()

    const [scheme] = useBuilderColorSchemeV2()
    const mode = scheme.current

    const currentStatusBarColor = statusBarColor[mode]

    switch (viewport) {
      case 'tv':
        return (
          <DesktopFrame
            statusBarColor={currentStatusBarColor}
            scale={deviceScale}
            bgColor={activePage?.bgColor}
            withSearch={activePage?.tbType === TopBarType.SearchBar}
            isFullScreen={isFullScreen}
            deviceRef={deviceRef as React.RefObject<HTMLDivElement>}
          >
            {children}
          </DesktopFrame>
        )

      case 'desktop':
        return (
          <DesktopFrame
            statusBarColor={currentStatusBarColor}
            scale={deviceScale}
            bgColor={activePage?.bgColor}
            withSearch={activePage?.tbType === TopBarType.SearchBar}
            isFullScreen={isFullScreen}
            deviceRef={deviceRef as React.RefObject<HTMLDivElement>}
          >
            {children}
          </DesktopFrame>
        )

      case 'tablet':
        return (
          <TabletFrame
            statusBarColor={currentStatusBarColor}
            scale={deviceScale}
            bgColor={activePage?.bgColor}
            withSearch={activePage?.tbType === TopBarType.SearchBar}
            deviceRef={deviceRef as React.RefObject<HTMLDivElement>}
          >
            {children}
          </TabletFrame>
        )

      case 'tablet-landscape':
        return (
          <TabletFrame
            landscape
            statusBarColor={currentStatusBarColor}
            scale={deviceScale}
            bgColor={activePage?.bgColor}
            withSearch={activePage?.tbType === TopBarType.SearchBar}
            deviceRef={deviceRef as React.RefObject<HTMLDivElement>}
          >
            {children}
          </TabletFrame>
        )

      case 'phone-landscape':
        return (
          <MobileFrame
            landscape
            statusBarColor={currentStatusBarColor}
            scale={deviceScale}
            bgColor={activePage?.bgColor}
            withSearch={activePage?.tbType === TopBarType.SearchBar}
            deviceRef={deviceRef as React.RefObject<HTMLDivElement>}
          >
            {children}
          </MobileFrame>
        )

      default:
        return (
          <MobileFrame
            statusBarColor={currentStatusBarColor}
            scale={deviceScale}
            bgColor={activePage?.bgColor}
            withSearch={activePage?.tbType === TopBarType.SearchBar}
            deviceRef={deviceRef as React.RefObject<HTMLDivElement>}
          >
            {children}
          </MobileFrame>
        )
    }
  }
)

DeviceFrame.displayName = 'DeviceFrame'
